import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plane, Lock, AlertTriangle, X } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { assistanceApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useAssistancePanel } from '../contexts/AssistancePanelContext';
import { useCockpitExplorer } from '../hooks/useCockpitExplorer';
import CockpitLocatePanel from './CockpitLocatePanel';
import PaywallModal from './PaywallModal';
import { AbnormalProceduresBanner, AbnormalProceduresList, AbnormalProcedureDetail, CATEGORY_COLORS } from './AbnormalProcedurePanel';
import { MelBanner, MelList, MelDetail } from './MelPanel';

/**
 * InstructorPanel - In-Flight Assistance System
 *
 * ECAM fault lookup and quick rectification for the Fenix A320
 * Glass Cockpit redesign (Part B) - visual restyle only, logic unchanged.
 *
 * PRODUCT ACCESS: Requires 'core' product ownership or founder role
 */

const InstructorPanel = () => {
  const { slug: currentPhaseSlug } = useParams();
  const { user } = useAuth();
  const { locate, close: closePanel } = useAssistancePanel();
  const { data: explorerData } = useCockpitExplorer();

  // Check if user has access (owns core product OR is founder)
  const hasAccess = user?.role === 'founder' || (user?.products || []).includes('core');

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allFaults, setAllFaults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('ALL');
  const [selectedFault, setSelectedFault] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);

  // Abnormal Procedures section (added 2026-10-15): banner -> list -> detail
  const [assistanceView, setAssistanceView] = useState('faults'); // 'faults' | 'abnormal-list' | 'mel-list'
  const [abnormalProcedures, setAbnormalProcedures] = useState([]);
  const [selectedProcedure, setSelectedProcedure] = useState(null);

  // MEL section (added 2026-10-16): banner -> list -> detail
  const [melItems, setMelItems] = useState([]);
  const [selectedMelItem, setSelectedMelItem] = useState(null);

  // Format phase name for display
  const formatPhaseName = (slug) => {
    if (!slug) return 'UNKNOWN';
    return slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .toUpperCase();
  };

  // Load all faults and categories on mount
  useEffect(() => {
    if (!hasAccess) return;

    const loadData = async () => {
      try {
        const [faultsRes, catsRes, proceduresRes, melRes] = await Promise.all([
          assistanceApi.getFaults(),
          assistanceApi.getCategories(),
          assistanceApi.getAbnormalProcedures(),
          assistanceApi.getMelItems(),
        ]);
        setAllFaults(faultsRes.data.faults || []);
        setCategories(catsRes.data.categories || []);
        setAbnormalProcedures(proceduresRes.data.procedures || []);
        setMelItems(melRes.data.items || []);
      } catch (error) {
        console.error('Failed to load faults:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    loadData();
  }, [hasAccess]);

  // Search with debounce - only drives the global cross-type dropdown
  // while the main fault view is open. When the Abnormal Procedures or
  // MEL list is open, the search box instead filters that list locally
  // (see searchQuery passed to AbnormalProceduresList/MelList below) and
  // this effect is a no-op so no API call fires and no dropdown opens.
  useEffect(() => {
    if (!hasAccess) return;

    if (assistanceView !== 'faults') {
      setSearchResults([]);
      return;
    }

    const searchFaults = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await assistanceApi.searchFaults(searchQuery);
        setSearchResults(response.data.results || []);
      } catch (error) {
        console.error('Failed to search faults:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(searchFaults, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, hasAccess, assistanceView]);

  // Filter faults by category
  const filteredFaults = useCallback(() => {
    if (activeCategory === 'ALL') return allFaults;
    return allFaults.filter(f => f.system === activeCategory);
  }, [allFaults, activeCategory]);

  // Handle category change
  const handleCategoryChange = async (category) => {
    setActiveCategory(category);
    setSelectedFault(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle fault selection
  const selectFault = (fault) => {
    setSelectedFault(fault);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle abnormal procedure selection (from the list, or from a search result)
  const selectProcedure = (procedure) => {
    setSelectedProcedure(procedure);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle MEL item selection (from the list, or from a search result)
  const selectMelItem = (item) => {
    setSelectedMelItem(item);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Open a linked MEL item from the ECAM fault detail chip (added 2026-10-16)
  const openMelFromFault = (melId) => {
    const item = melItems.find((m) => m.id === melId);
    if (!item) return;
    setSelectedFault(null);
    selectMelItem(item);
  };

  const severityColor = (severity) => severity === 'warning' ? 'var(--gc-warning)' : 'var(--gc-amber)';
  const severityBorder = (severity) => severity === 'warning' ? 'var(--gc-warning-border)' : 'var(--gc-amber-badge-border)';
  const severityLabel = (severity) => severity === 'warning' ? 'WARNING' : 'CAUTION';

  const SeverityBadge = ({ severity }) => (
    <span
      className="font-plex-mono text-[10px] font-semibold px-1.5 py-0.5 rounded-gc-badge border uppercase tracking-wider"
      style={{ color: severityColor(severity), borderColor: severityBorder(severity) }}
    >
      {severityLabel(severity)}
    </span>
  );

  // Checklist locate mode: shows a Cockpit Explorer control/sequence in
  // this same panel slot instead of fault search, independent of
  // 'core' access (the cockpit explorer itself is not paywalled).
  if (locate && explorerData) {
    return (
      <div className="h-full flex flex-col p-6 overflow-y-auto" data-testid="instructor-panel">
        <div className="flex items-center justify-end mb-2 flex-shrink-0">
          <button
            onClick={closePanel}
            className="p-1.5 rounded-gc-button transition-colors"
            style={{ color: 'var(--gc-text-muted)' }}
            aria-label="Close panel"
            data-testid="close-assistance-panel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <CockpitLocatePanel locate={locate} explorerData={explorerData} />
      </div>
    );
  }

  return (
    <div className={`flex flex-col p-6 ${!hasAccess ? 'h-full' : ''}`} data-testid="instructor-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h3 className="font-barlow-condensed font-bold text-[22px]" style={{ color: 'var(--gc-text-main)' }}>
            In-Flight Assistance
          </h3>
          <p className="text-sm mt-1" style={{ color: 'var(--gc-text-muted)' }}>
            ECAM fault lookup for the Fenix A320
          </p>
        </div>
        <button
          onClick={closePanel}
          className="p-1.5 rounded-gc-button transition-colors flex-shrink-0"
          style={{ color: 'var(--gc-text-muted)' }}
          aria-label="Close panel"
          data-testid="close-assistance-panel"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* LOCKED STATE - same visual language as the locked phase page */}
      {!hasAccess ? (
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center" data-testid="assistance-locked">
          <div
            className="w-14 h-14 rounded-[14px] flex items-center justify-center mb-5"
            style={{ background: 'var(--gc-current-row)', border: '1px solid #1F5566' }}
          >
            <Lock className="w-6 h-6" style={{ color: 'var(--gc-cyan)' }} strokeWidth={1.8} />
          </div>
          <h4 className="font-barlow-condensed font-bold text-lg mb-2" style={{ color: 'var(--gc-text-main)' }}>
            Feature Locked
          </h4>
          <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--gc-text-muted)' }}>
            Unlock the Fenix A320 Edition to access fault diagnosis and in-flight assistance tools.
          </p>
          <button
            onClick={() => setShowPaywall(true)}
            className="h-11 px-5 rounded-gc-button font-barlow-condensed font-bold text-base"
            style={{ background: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }}
            data-testid="unlock-full-access-btn-assistance"
          >
            Unlock Full Access
          </button>
        </div>
      ) : (
        /* UNLOCKED STATE - part of the single scroll area provided by the
           DashboardLayout `h-full overflow-y-auto` wrapper around
           InstructorPanel (added 2026-10-16: no nested flex-1/overflow-y-auto
           height constraints here or below, so header, phase card, search,
           banners, filter pills and the full list all scroll together). */
        <div className="flex flex-col">
          {/* Current Phase Display */}
          <div
            className="mb-4 p-3 rounded-gc-card border"
            style={{ background: 'var(--gc-current-row)', borderColor: 'var(--gc-border-outline)' }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Plane className="w-3.5 h-3.5" style={{ color: 'var(--gc-cyan)' }} />
              <span className="font-plex-mono text-[10px] uppercase tracking-wider" style={{ color: 'var(--gc-cyan)' }}>
                Current Phase
              </span>
            </div>
            <p className="font-barlow-condensed font-bold text-sm" style={{ color: 'var(--gc-text-main)' }}>
              {formatPhaseName(currentPhaseSlug)}
            </p>
          </div>

          {/* Fault Search */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Search className="w-3.5 h-3.5" style={{ color: 'var(--gc-text-muted)' }} />
              <span className="font-plex-mono text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--gc-text-muted)' }}>
                Fault Search
              </span>
            </div>
            <p className="text-xs mb-2 leading-relaxed" style={{ color: 'var(--gc-text-muted)' }}>
              Enter any ECAM message or PFD annunciation to find troubleshooting guidance.
            </p>

            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                placeholder="e.g. ENG 1 FAULT, BLEED, HYD..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-11 px-3.5 text-sm rounded-[10px] border focus:outline-none focus:ring-1"
                style={{ background: '#0E131C', borderColor: '#263042', color: 'var(--gc-text-main)' }}
                data-testid="ecam-search-input"
              />

              {/* Search Results Dropdown */}
              {searchResults.length > 0 && searchQuery.length >= 2 && (
                <div
                  className="absolute z-20 w-full mt-1.5 rounded-gc-card border shadow-lg max-h-48 overflow-y-auto"
                  style={{ background: 'var(--gc-card)', borderColor: 'var(--gc-border-card)' }}
                >
                  {searchResults.map((result, index) => (
                    result.type === 'abnormal_procedure' ? (
                      <button
                        key={result.id || index}
                        onClick={() => selectProcedure(result)}
                        className="w-full px-3.5 py-2.5 text-left transition-colors border-b last:border-0"
                        style={{ borderColor: 'var(--gc-border-row)' }}
                        data-testid={`search-result-${index}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-plex-mono text-xs font-bold" style={{ color: 'var(--gc-text-main)' }}>
                            {result.title}
                          </span>
                          <span
                            className="font-plex-mono text-[10px] font-semibold px-1.5 py-0.5 rounded-gc-badge border uppercase"
                            style={{ color: CATEGORY_COLORS[result.category], borderColor: CATEGORY_COLORS[result.category] }}
                          >
                            {result.category}
                          </span>
                        </div>
                        <p className="text-[11px] mt-0.5" style={{ color: 'var(--gc-text-muted)' }}>QRC {String(result.number).padStart(2, '0')} · {result.subtitle}</p>
                      </button>
                    ) : result.type === 'mel_item' ? (
                      <button
                        key={result.id || index}
                        onClick={() => selectMelItem(result)}
                        className="w-full px-3.5 py-2.5 text-left transition-colors border-b last:border-0"
                        style={{ borderColor: 'var(--gc-border-row)' }}
                        data-testid={`search-result-${index}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-plex-mono text-xs font-bold" style={{ color: 'var(--gc-text-main)' }}>
                            {result.title}
                          </span>
                          <span
                            className="font-plex-mono text-[10px] font-semibold px-1.5 py-0.5 rounded-gc-badge border uppercase"
                            style={{ color: 'var(--gc-cyan)', borderColor: 'var(--gc-cyan)' }}
                          >
                            MEL
                          </span>
                        </div>
                        <p className="text-[11px] mt-0.5" style={{ color: 'var(--gc-text-muted)' }}>{result.chapter}</p>
                      </button>
                    ) : (
                      <button
                        key={result.id || index}
                        onClick={() => selectFault(result)}
                        className="w-full px-3.5 py-2.5 text-left transition-colors border-b last:border-0"
                        style={{ borderColor: 'var(--gc-border-row)' }}
                        data-testid={`search-result-${index}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-plex-mono text-xs font-bold" style={{ color: 'var(--gc-text-main)' }}>
                            {result.fault}
                          </span>
                          <SeverityBadge severity={result.severity} />
                        </div>
                        <p className="text-[11px] mt-0.5" style={{ color: 'var(--gc-text-muted)' }}>{result.system}</p>
                      </button>
                    )
                  ))}
                </div>
              )}

              {loading && searchQuery.length >= 2 && (
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--gc-border-outline)', borderTopColor: 'var(--gc-cyan)' }} />
                </div>
              )}
            </div>
          </div>

          {/* Abnormal Procedures banner (added 2026-10-15) */}
          {assistanceView === 'faults' && (
            <AbnormalProceduresBanner onClick={() => setAssistanceView('abnormal-list')} />
          )}

          {/* MEL banner (added 2026-10-16) */}
          {assistanceView === 'faults' && (
            <MelBanner onClick={() => setAssistanceView('mel-list')} />
          )}

          {assistanceView === 'abnormal-list' ? (
            <AbnormalProceduresList
              procedures={abnormalProcedures}
              onSelect={selectProcedure}
              onBack={() => setAssistanceView('faults')}
              searchQuery={searchQuery}
            />
          ) : assistanceView === 'mel-list' ? (
            <MelList
              items={melItems}
              onSelect={selectMelItem}
              onBack={() => setAssistanceView('faults')}
              searchQuery={searchQuery}
            />
          ) : (
          <>
          {/* Category Filter Buttons - Hidden when actively searching */}
          {!(searchQuery.length >= 2) && (
            <div className="flex flex-wrap gap-1.5 mb-3 pb-2">
              <button
                onClick={() => handleCategoryChange('ALL')}
                className="font-plex-mono text-[10px] px-2.5 py-1 rounded-gc-badge border uppercase tracking-wider transition-colors"
                style={
                  activeCategory === 'ALL'
                    ? { background: 'var(--gc-cyan)', borderColor: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }
                    : { borderColor: '#263042', color: 'var(--gc-text-muted)' }
                }
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryChange(cat.name)}
                  className="font-plex-mono text-[10px] px-2.5 py-1 rounded-gc-badge border uppercase tracking-wider transition-colors"
                  style={
                    activeCategory === cat.name
                      ? { background: 'var(--gc-cyan)', borderColor: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }
                      : { borderColor: '#263042', color: 'var(--gc-text-muted)' }
                  }
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {/* Results Count - Hidden when actively searching */}
          {!(searchQuery.length >= 2) && (
            <div
              className="font-plex-mono text-[10px] uppercase tracking-wider mb-2 pb-2 border-b"
              style={{ color: 'var(--gc-text-faint)', borderColor: 'var(--gc-border-row)' }}
            >
              Showing {filteredFaults().length} fault{filteredFaults().length !== 1 ? 's' : ''}
            </div>
          )}

          {/* Fault List - Hidden when actively searching */}
          {!(searchQuery.length >= 2) && (
            <div className="-mx-1 px-1">
              {initialLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--gc-border-outline)', borderTopColor: 'var(--gc-cyan)' }} />
                </div>
              ) : filteredFaults().length === 0 ? (
                <div className="text-center py-8 text-sm" style={{ color: 'var(--gc-text-faint)' }}>
                  No faults in this category
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredFaults().map((fault, index) => (
                    <button
                      key={fault.id || index}
                      onClick={() => selectFault(fault)}
                      className="w-full p-3 text-left rounded-gc-card transition-colors"
                      style={{ background: selectedFault?.id === fault.id ? 'var(--gc-current-row)' : 'transparent' }}
                      data-testid={`fault-item-${index}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-plex-mono text-xs font-bold" style={{ color: 'var(--gc-text-main)' }}>
                          {fault.fault}
                        </span>
                        <SeverityBadge severity={fault.severity} />
                      </div>
                      <p className="text-[11px] mt-1" style={{ color: 'var(--gc-text-muted)' }}>{fault.system}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* Selected Fault Detail Panel - Slides in from right */}
      {selectedFault && hasAccess && (
        <FaultDetailPanel fault={selectedFault} melItems={melItems} onOpenMel={openMelFromFault} onClose={() => setSelectedFault(null)} />
      )}

      {/* Selected Abnormal Procedure Detail Panel - Slides in from right */}
      {selectedProcedure && hasAccess && (
        <AbnormalProcedureDetail procedure={selectedProcedure} onClose={() => setSelectedProcedure(null)} />
      )}

      {/* Selected MEL Item Detail Panel - Slides in from right */}
      {selectedMelItem && hasAccess && (
        <MelDetail item={selectedMelItem} onClose={() => setSelectedMelItem(null)} />
      )}

      {/* Paywall checkout modal - reuses the existing Stripe checkout flow */}
      <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />
    </div>
  );
};


/**
 * FaultDetailPanel - Displays selected fault details
 */
const FaultDetailPanel = (props) => {
  const fault = props.fault;
  const onClose = props.onClose;

  const severityColor = fault.severity === 'warning' ? 'var(--gc-warning)' : 'var(--gc-amber)';
  const severityBorder = fault.severity === 'warning' ? 'var(--gc-warning-border)' : 'var(--gc-amber-badge-border)';
  const severityLabel = fault.severity === 'warning' ? 'WARNING' : 'CAUTION';

  // Linked MEL item (added 2026-10-16): optional `mel_id` set by the
  // ecam_mel_links_2026_10_16 startup content update.
  const linkedMel = fault.mel_id ? (props.melItems || []).find((m) => m.id === fault.mel_id) : null;
  const melIsNogo = linkedMel?.kind === 'nogo';
  const melChipColor = melIsNogo ? '#F87171' : '#34D399';

  return (
    <div
      className="fixed inset-y-0 right-0 w-full md:w-[450px] lg:w-[500px] border-l shadow-2xl z-50 overflow-y-auto"
      style={{ background: 'var(--gc-bg)', borderColor: 'var(--gc-border-nav)' }}
      data-testid="fault-detail-panel"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-gc-button z-10"
        style={{ color: 'var(--gc-text-muted)' }}
        data-testid="close-fault-detail"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="p-6 md:p-8">
        {/* Fault Title */}
        <h2
          className="font-plex-mono text-xl md:text-2xl font-bold tracking-wide mb-4"
          style={{ color: severityColor }}
        >
          {fault.fault}
        </h2>

        {/* Linked MEL chip (if mel_id is set) */}
        {linkedMel && (
          <button
            onClick={() => props.onOpenMel(fault.mel_id)}
            className="w-full flex items-center gap-2.5 p-3 rounded-gc-card border mb-4 text-left transition-colors hover:opacity-90"
            style={{ background: 'var(--gc-card)', borderColor: melChipColor }}
            data-testid="fault-mel-link-chip"
          >
            <span
              className="font-plex-mono text-[10px] font-bold px-2 py-0.5 rounded-gc-badge border uppercase tracking-wider shrink-0"
              style={{ color: melChipColor, borderColor: melChipColor }}
            >
              {melIsNogo ? 'MEL · NO-GO' : 'MEL · CAN FLY'}
            </span>
            <span className="font-plex-mono text-[11px] flex-1 min-w-0 truncate" style={{ color: 'var(--gc-text-muted)' }}>
              {melIsNogo ? 'Fix before flight' : `Cat ${linkedMel.category} · ${linkedMel.repair}`}
            </span>
          </button>
        )}

        {/* Badges */}
        <div className="flex items-center gap-2 mb-6 pb-4 border-b" style={{ borderColor: 'var(--gc-border-row)' }}>
          <span
            className="font-plex-mono text-[10px] px-2.5 py-1 rounded-gc-badge border font-semibold uppercase tracking-wider"
            style={{ color: 'var(--gc-text-muted)', borderColor: 'var(--gc-border-outline)' }}
          >
            {fault.system}
          </span>
          <span
            className="font-plex-mono text-[10px] px-2.5 py-1 rounded-gc-badge border font-semibold uppercase tracking-wider"
            style={{ color: severityColor, borderColor: severityBorder }}
          >
            {severityLabel}
          </span>
        </div>

        {/* Caution Box (if present) */}
        {fault.caution && (
          <div
            className="mb-6 p-4 rounded-gc-card border flex gap-3"
            style={{ background: 'var(--gc-card)', borderColor: 'var(--gc-warning-border)' }}
          >
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--gc-warning)' }} />
            <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>
              <span className="font-semibold" style={{ color: 'var(--gc-warning)' }}>CAUTION:</span> {fault.caution}
            </p>
          </div>
        )}

        {/* What This Means */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="font-plex-mono text-[11px] uppercase tracking-wider" style={{ color: 'var(--gc-text-muted)' }}>
              What This Means
            </span>
            <div className="flex-1 h-px" style={{ background: 'var(--gc-border-row)' }} />
          </div>
          <div className="p-4 rounded-gc-card border" style={{ background: 'var(--gc-card)', borderColor: 'var(--gc-border-card)' }}>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>
              {fault.what_this_means}
            </p>
          </div>
        </div>

        {/* Quick Rectification */}
        {fault.actions && fault.actions.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-plex-mono text-[11px] uppercase tracking-wider" style={{ color: 'var(--gc-text-muted)' }}>
                Quick Rectification
              </span>
              <div className="flex-1 h-px" style={{ background: 'var(--gc-border-row)' }} />
            </div>
            <div className="space-y-2">
              {fault.actions.map((action, index) => (
                <div
                  key={index}
                  className="flex gap-3 p-3 rounded-gc-card border"
                  style={{ background: 'var(--gc-card)', borderColor: 'var(--gc-border-card)' }}
                >
                  <span className="font-plex-mono text-sm font-bold w-6 flex-shrink-0" style={{ color: 'var(--gc-cyan)' }}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>
                    {action}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Note (if present) */}
        {fault.note && (
          <div className="p-4 rounded-gc-card border" style={{ background: 'var(--gc-card)', borderColor: 'var(--gc-border-card)' }}>
            <div className="font-plex-mono text-[11px] uppercase tracking-wider mb-2" style={{ color: 'var(--gc-text-body)' }}>
              Note
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-muted)' }}>
              {fault.note}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorPanel;
